import {useEffect, useState} from 'react';
import {Add} from '@mui/icons-material';
import {Alert, Box, Button, Card, IconButton, Stack, Tooltip, Typography} from '@mui/material';
import {FiEdit, FiTrash2} from 'react-icons/fi';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import {ReminderRecurrenceType} from '../../../../../../backend/interfaces/reminder-recurrence-type';
import {RolePermissionType} from '../../../../../../backend/enums/role-permission-type';
import {ContentPage} from '../../../../../../components/content-page/content-page';
import GenericHeader from '../../../../../../components/generic-header/generic-header';
import {LoadingContent} from '../../../../../../components/loading-content/loading-content';
import {useAuth} from '../../../../../../contexts/auth/context';
import {translations} from '../../../../../../i18n/translations';
import {RemindersRepository} from '../../../../../../repositories/reminders';
import {UsersRepository} from '../../../../../../repositories/users';
import {BulkRemindersModal} from '../../seniors/modals/bulk-reminders-modal';
import {plannerListSectionHeaderSx} from '../../planner/calendar/body/planner-list/planner-list-date-item';
import {
  PlannerListAvatars,
  PlannerListRow,
  plannerListTextSx,
} from '../../planner/calendar/body/planner-list/planner-list-row';
import {GroupReminderModal, GroupRow} from './group-reminder-modal';
import {GroupReminder, reminderGroupKey} from './reminder-groups';

export function MyRemindersScreen({entityId}: {entityId: string}) {
  const {t} = useTranslation();
  const {state, hasWritePermission} = useAuth();
  const [rows, setRows] = useState<GroupRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reload, setReload] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [selected, setSelected] = useState<{rows: GroupRow[]; deleting: boolean}>();
  const canWrite = hasWritePermission(RolePermissionType.Seniors_PersonalData_Reminders);
  const currentUserId = state.session?.currentUser.id;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setRows([]);
    const load = async () => {
      const reminders: GroupReminder[] = [];
      let total = 0;
      do {
        const page = await RemindersRepository.instance.get(20, reminders.length);
        total = page.totalCount;
        if (!page.data.length && reminders.length < total)
          throw new Error('Incomplete reminders list');
        reminders.push(...(page.data as GroupReminder[]));
      } while (reminders.length < total);
      // Legacy reminders without an entity remain visible in each entity context.
      const scopedReminders = reminders.filter(
        (reminder) => !reminder.entityId || reminder.entityId === entityId
      );
      const users = await Promise.all(
        Array.from(new Set(scopedReminders.map((r) => r.toUserId))).map(async (id) => {
          const user = await UsersRepository.instance.getUserPublicInfo(id).catch(() => null);
          return [id, {name: user?.name || id, picture: user?.picture}] as const;
        })
      );
      const byId = new Map(users);
      return scopedReminders.map((reminder) => ({
        reminder,
        recipient: byId.get(reminder.toUserId)!.name,
        picture: byId.get(reminder.toUserId)!.picture,
      }));
    };
    void load()
      .then((result) => {
        if (!cancelled) setRows(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reload, entityId]);

  const groups = new Map<string, GroupRow[]>();
  for (const row of rows) {
    const key = reminderGroupKey(row.reminder);
    groups.set(key, [...(groups.get(key) || []), row]);
  }
  return (
    <ContentPage fullWidth>
      <GenericHeader
        isLoading={loading}
        pageTitle={t(translations.reminders.my_title)}
        leftColumn={<Box />}
        rightColumn={
          canWrite && currentUserId ? (
            <Button variant="inverted" startIcon={<Add />} onClick={() => setShowModal(true)}>
              {t(translations.reminders.bulk_title)}
            </Button>
          ) : undefined
        }
      />
      <Stack sx={{mt: '14px', minWidth: 0}}>
        <Typography variant="caption" color="text.secondary" sx={{px: '9px', mb: '10px'}}>
          <strong>Nota interna:</strong> devido a uma limitação do endpoint de listagem da API,
          apenas são apresentados os lembretes criados pelo utilizador com sessão iniciada. O
          agrupamento atual é aproximado e feito apenas na interface, sem usar uma associação
          explícita de grupo na API. Por isso, lembretes independentes com as mesmas definições
          podem aparecer agrupados.
        </Typography>
        <Card
          elevation={0}
          sx={{
            px: '9px',
            py: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '5px',
            minHeight: 'calc(100vh - 210px)',
          }}
        >
          {error && (
            <Alert
              severity="error"
              action={
                <Button onClick={() => setReload((n) => n + 1)}>
                  {t(translations.reminders.retry)}
                </Button>
              }
            >
              {t(translations.reminders.load_error)}
            </Alert>
          )}
          {loading && <LoadingContent marginTop="24px" />}
          {!loading && !error && !rows.length && (
            <Typography
              sx={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}
            >
              {t(translations.reminders.empty_title)}
            </Typography>
          )}
          {!loading && !error && rows.length > 0 && (
            <Box sx={{width: '100%', minWidth: 0, overflowX: 'auto'}}>
              <Box sx={{minWidth: 850}}>
                <Stack
                  direction="row"
                  sx={{
                    ...plannerListSectionHeaderSx,
                    mt: '6px',
                    mb: '10px',
                    '& > *': {overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'},
                  }}
                >
                  <Box sx={{flex: 0.5, mr: '23px'}}>{t(translations.reminders.group_time)}</Box>
                  <Box sx={{flex: 2, mr: '15px'}}>{t(translations.reminders.group_reminder)}</Box>
                  <Box sx={{flex: 2, mr: '15px'}}>{t(translations.reminders.group_repeats)}</Box>
                  <Box sx={{flex: 1, minWidth: 120, mr: '15px'}}>
                    {t(translations.reminders.group_recipients)}
                  </Box>
                  <Box sx={{width: 100, flexShrink: 0}}>
                    {t(translations.reminders.group_actions)}
                  </Box>
                </Stack>
                {Array.from(groups.entries()).map(([key, group]) => {
                  const r = group[0].reminder;
                  const recipients = group.filter(
                    (row, index) =>
                      group.findIndex(
                        (item) => item.reminder.toUserId === row.reminder.toUserId
                      ) === index
                  );
                  const manageable = canWrite;
                  const labels: Record<string, string> = {
                    [ReminderRecurrenceType.Once]: translations.reminders.recurrence.once,
                    [ReminderRecurrenceType.PerMinute]:
                      translations.reminders.recurrence.per_minute,
                    [ReminderRecurrenceType.Hourly]: translations.reminders.recurrence.hourly,
                    [ReminderRecurrenceType.Daily]: translations.reminders.recurrence.daily,
                    [ReminderRecurrenceType.Weekly]: translations.reminders.recurrence.weekly,
                    [ReminderRecurrenceType.Monthly]: translations.reminders.recurrence.monthly,
                    [ReminderRecurrenceType.Yearly]: translations.reminders.recurrence.yearly,
                  };
                  const openEditor = () => setSelected({rows: group, deleting: false});
                  return (
                    <PlannerListRow
                      key={key}
                      role={manageable ? 'button' : undefined}
                      tabIndex={manageable ? 0 : undefined}
                      aria-label={manageable ? t(translations.reminders.edit) : undefined}
                      onClick={manageable ? openEditor : undefined}
                      onKeyDown={
                        manageable
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openEditor();
                              }
                            }
                          : undefined
                      }
                    >
                      <Typography noWrap sx={{...plannerListTextSx, flex: 0.5, mr: '23px'}}>
                        {moment(r.startAt).format('HH:mm')}
                      </Typography>
                      <Tooltip title={r.message}>
                        <Box
                          sx={{
                            flex: 2,
                            mr: '15px',
                            minWidth: 0,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <Typography noWrap sx={{...plannerListTextSx, minWidth: 0}}>
                            {r.title}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Typography noWrap sx={{...plannerListTextSx, flex: 2, mr: '15px'}}>
                        {labels[r.recurrenceType]
                          ? t(labels[r.recurrenceType], {count: r.recurrenceCount})
                          : t(translations.reminders.recurrence.current_unsupported, {
                              value: r.recurrenceType,
                            })}
                      </Typography>
                      <PlannerListAvatars
                        users={recipients.map((row) => ({
                          id: row.reminder.toUserId,
                          name: row.recipient,
                          picture: row.picture,
                        }))}
                      />
                      <Box sx={{width: 100, flexShrink: 0, display: 'flex', position: 'relative'}}>
                        <Box sx={{position: 'relative', display: 'inline-flex'}}>
                          {manageable && (
                            <>
                              <Tooltip title={t(translations.reminders.edit)}>
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  sx={{color: 'text.primary'}}
                                  aria-label={t(translations.reminders.edit)}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openEditor();
                                  }}
                                >
                                  <FiEdit size={18} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t(translations.reminders.delete_title)}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={t(translations.reminders.delete_title)}
                                  onKeyDown={(event) => event.stopPropagation()}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setSelected({rows: group, deleting: true});
                                  }}
                                >
                                  <FiTrash2 size={18} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          {!r.entityId && (
                            <Box
                              component="span"
                              role="img"
                              aria-label={t(translations.reminders.no_entity_assigned)}
                              sx={{
                                position: 'absolute',
                                left: '100%',
                                ml: '6px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: 5,
                                height: 5,
                                borderRadius: '50%',
                                bgcolor: 'gray.general',
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </PlannerListRow>
                  );
                })}
              </Box>
            </Box>
          )}
        </Card>
      </Stack>
      {showModal && currentUserId && (
        <BulkRemindersModal
          entityId={entityId}
          currentUserId={currentUserId}
          onClose={(changed) => {
            setShowModal(false);
            if (changed) setReload((n) => n + 1);
          }}
        />
      )}
      {selected && (
        <GroupReminderModal
          rows={selected.rows}
          deleting={selected.deleting}
          entityId={entityId}
          onClose={(changed) => {
            setSelected(undefined);
            if (changed) setReload((n) => n + 1);
          }}
        />
      )}
    </ContentPage>
  );
}
